import React, { useState, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "../styles/Calendar.css";
import { FaCalendarAlt, FaClock, FaUsers, FaMapMarkerAlt, FaShoppingCart } from "react-icons/fa";
import { db } from "../firebase";
import { collection, getDocs, deleteDoc, doc } from "firebase/firestore";
import EditBookingModal from "./EditBookingModal"; // Import the modal component

const Schedule = () => {
  const [date, setDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentBooking, setCurrentBooking] = useState(null);
  const [reload, setReload] = useState(false); // Add this state
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);

  const getCartName = (cart) => {
    return cart === "1" ? "Vagn 1&2" : "Vagn 3&4";
  };

  useEffect(() => {
    const fetchBookings = async () => {
      const querySnapshot = await getDocs(collection(db, "booking"));
      const fetchedBookings = querySnapshot.docs
        .map((doc) => {
          const data = doc.data();
          const startTime = data.starttime;
          const duration = data.duration;

          // Parse the date string
          const dateString = data.date;
          const [day, month, year, , time] = dateString.split(/[\s,]+/);
          const [hours, minutes, seconds] = time.split(":").map(Number);

          // Create a Date object
          const monthIndex = new Date(
            Date.parse(month + " 1, 2024")
          ).getMonth();
          const startDate = new Date(
            year,
            monthIndex,
            parseInt(day),
            hours,
            minutes,
            seconds
          );

          if (typeof startTime === "string" && typeof duration === "number") {
            const [startHours, startMinutes] = startTime.split(":").map(Number);
            const durationInMs = duration * 60 * 60 * 1000;
            startDate.setHours(startHours, startMinutes);
            const endDate = new Date(startDate.getTime() + durationInMs);
            const formattedTime = `${startDate
              .toTimeString()
              .slice(0, 5)}-${endDate.toTimeString().slice(0, 5)}`;
            return {
              dataDate: data.date,
              date: startDate,
              title: `${data.persons} - ${getCartName(data.cart)}`,
              time: formattedTime,
              id: doc.id,
              starttime: data.starttime,
              duration: data.duration,
              persons: data.persons,
              location: data.location,
              cart: data.cart,
              dateString: data.dateString,
            };
          } else {
            console.warn("Invalid booking data:", data);
            return null;
          }
        })
        .filter((booking) => booking !== null);
      setBookings(fetchedBookings);
    };

    fetchBookings();
  }, [reload]);

  const getTileContent = ({ date, view }) => {
    if (view === "month") {
      const bookingsForDay = bookings.filter(
        (booking) => booking.date.toDateString() === date.toDateString()
      );
      return bookingsForDay.length > 0 ? (
        <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-5 h-5 bg-primary-600 text-white rounded-md flex items-center justify-center text-[10px] font-bold shadow-sm z-10">
          {bookingsForDay.length}
        </div>
      ) : null;
    }
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
  };

  const getDailyBookings = () => {
    return bookings
      .filter(
        (booking) => booking.date.toDateString() === selectedDate.toDateString()
      )
      .sort((a, b) => a.date - b.date); // Sort by date (time)
  };

  const handleEditBooking = (booking) => {
    setCurrentBooking(booking);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentBooking(null);
  };

  const handleSaveBooking = (updatedBooking) => {
    setReload((prev) => !prev); // Toggle the reload state to trigger useEffect
    handleCloseModal();
  };

  const handleDeleteBooking = async () => {
    if (bookingToDelete) {
      await deleteDoc(doc(db, "booking", bookingToDelete.id));
      setReload((prev) => !prev); // Reload bookings
      setIsConfirmDialogOpen(false);
      setBookingToDelete(null);
    }
  };

  const openConfirmDialog = (booking) => {
    setBookingToDelete(booking);
    setIsConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setIsConfirmDialogOpen(false);
    setBookingToDelete(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 animate-fade-in">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2 flex items-center">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mr-4">
              <FaCalendarAlt className="text-primary-600 text-xl" />
            </div>
            Schema
          </h2>
          <p className="text-gray-600 ml-16">Visa och hantera alla bokningar</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar Section */}
          <div className="lg:col-span-2">
            <div className="card animate-slide-up p-0 overflow-hidden">
              <div className="bg-gradient-to-r from-primary-50 to-blue-50 px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Kalender</h3>
                <p className="text-sm text-gray-600 mt-1">Klicka på ett datum för att se bokningar</p>
              </div>
              <div className="custom-calendar-container p-4">
                <Calendar
                  onChange={setDate}
                  value={date}
                  tileContent={getTileContent}
                  onClickDay={handleDateClick}
                  className="custom-calendar w-full"
                  locale="sv-SE"
                  showNeighboringMonth={true}
                />
              </div>
            </div>
          </div>

          {/* Bookings List */}
          <div className="lg:col-span-1">
            {selectedDate ? (
              <div className="card animate-fade-in">
                <div className="mb-4 pb-4 border-b border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    {selectedDate
                      .toLocaleDateString("sv-SE", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                      .split(" ")
                      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(" ")}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {getDailyBookings().length} {getDailyBookings().length === 1 ? 'bokning' : 'bokningar'}
                  </p>
                </div>
                {getDailyBookings().length > 0 ? (
                  <ul className="space-y-3">
                    {getDailyBookings().map((booking, index) => (
                      <li
                        key={index}
                        className="bg-gradient-to-br from-gray-50 to-white p-4 rounded-lg border border-gray-200 hover:shadow-md hover:border-primary-300 transition-all duration-200 transform hover:-translate-y-0.5"
                      >
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                              <span className="font-semibold text-gray-900">{booking.time}</span>
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <div className="flex items-center text-sm text-gray-600">
                              <FaUsers className="mr-2 text-primary-500" />
                              <span>{booking.persons}</span>
                            </div>
                            <div className="flex items-center text-sm text-gray-600">
                              <FaShoppingCart className="mr-2 text-primary-500" />
                              <span>{getCartName(booking.cart)}</span>
                            </div>
                            <div className="flex items-center text-sm text-gray-600">
                              <FaMapMarkerAlt className="mr-2 text-primary-500" />
                              <span className="truncate">{booking.location || 'Vasaparken'}</span>
                            </div>
                          </div>

                          {booking.date > new Date() && (
                            <div className="flex space-x-2 pt-2 border-t border-gray-200">
                              <button
                                onClick={() => handleEditBooking(booking)}
                                className="flex-1 bg-primary-600 hover:bg-primary-700 text-white text-sm px-3 py-2 rounded-lg transition-all duration-200 font-medium"
                              >
                                Ändra
                              </button>
                              <button
                                onClick={() => openConfirmDialog(booking)}
                                className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm px-3 py-2 rounded-lg transition-all duration-200 font-medium"
                              >
                                Avboka
                              </button>
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-center py-8">
                    <FaCalendarAlt className="text-4xl text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Inga bokningar för detta datum</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="card animate-fade-in">
                <div className="text-center py-8">
                  <FaCalendarAlt className="text-4xl text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Välj ett datum för att se bokningar</p>
                </div>
              </div>
            )}
          </div>
        </div>
        {isModalOpen && currentBooking && (
          <EditBookingModal
            booking={currentBooking}
            onClose={handleCloseModal}
            onSave={handleSaveBooking}
          />
        )}
        {isConfirmDialogOpen && bookingToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 animate-fade-in">
            <div className="bg-white rounded-xl shadow-large max-w-md w-full p-6 animate-scale-in">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Bekräfta avbokning</h3>
              <p className="text-gray-600 mb-4">Är du säker på att du vill avboka denna bokning?</p>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-6 border border-gray-200">
                <div className="space-y-2">
                  <div className="flex items-center text-sm">
                    <FaClock className="mr-2 text-primary-500" />
                    <span className="font-medium text-gray-900">{bookingToDelete.time}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <FaUsers className="mr-2 text-primary-500" />
                    <span>{bookingToDelete.persons}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <FaShoppingCart className="mr-2 text-primary-500" />
                    <span>{getCartName(bookingToDelete.cart)}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <FaMapMarkerAlt className="mr-2 text-primary-500" />
                    <span>{bookingToDelete.location || 'Vasaparken'}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={closeConfirmDialog}
                  className="btn-secondary flex-1"
                >
                  Avbryt
                </button>
                <button
                  onClick={handleDeleteBooking}
                  className="btn-danger flex-1"
                >
                  Bekräfta avbokning
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Schedule;
